import { DecimalPipe } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { DateFormat } from '@models/date-format';
import { Patient } from '@models/patient';
import { Expense, ReimbursementRequest } from '@models/request';
import { StudyExpense, VisitName } from '@models/study';
import { FileDownloadService } from '@services/file-download/file-download.service';
import { Moment, isMoment } from 'moment';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';
import {
    expensesTableLayout,
    header,
    marginSummary,
    marginTable,
    plainLayout,
    reportStyles,
    spacer,
} from './pdf-generation-models/report-layout';

export type ExpenseReportData = {
    request: ReimbursementRequest;
    patient: Patient;
    visitName: VisitName | undefined;
    availableExpenses: StudyExpense[];
    attachmentUrls: string[];
    /** patient-currency/USD amounts and totals; same rule as the request view (elevated access, In Process or higher) */
    showConvertedAmounts: boolean;
};

type Alignment = 'left' | 'center' | 'right';

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;

@Injectable({
    providedIn: 'root',
})
export class PdfGenerationService {
    private readonly downloadService = inject(FileDownloadService);
    private readonly datePipe = inject(dateFormatPipe);
    private readonly decimalPipe = new DecimalPipe('en-US');
    private pdfMake: any;

    /** Builds the expense report followed by all attachments (PDFs and JPG/PNG images) as a single PDF. */
    generateExpenseReport(data: ExpenseReportData): Observable<Blob> {
        const attachments$ = data.attachmentUrls.length
            ? forkJoin(data.attachmentUrls.map((url) => this.downloadAttachment(url)))
            : of([]);

        return attachments$.pipe(
            switchMap((attachments) => this.buildPdf(data, attachments)),
            map((bytes) => new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'application/pdf' }))
        );
    }

    private downloadAttachment(url: string): Observable<Blob | null> {
        return this.downloadService
            .downloadFile<Blob>(url, 'blob')
            .pipe(map(({ response }) => (response instanceof HttpResponse ? response.body : null)));
    }

    private async buildPdf(data: ExpenseReportData, attachments: (Blob | null)[]): Promise<Uint8Array> {
        const pdf = await PDFDocument.create();

        await this.appendPdf(pdf, await this.createReport(data));

        for (const [index, file] of attachments.entries()) {
            // one unreadable attachment (encrypted, corrupted, unsupported format) must not fail the whole report
            try {
                if (file?.type === 'application/pdf') await this.appendPdf(pdf, file);
                else if (file?.type.startsWith('image/')) await this.appendImage(pdf, file);
                else throw new Error(`Unsupported attachment type: ${file?.type}`);
            } catch {
                await this.appendPlaceholder(pdf, index + 1, file?.type);
            }
        }

        await this.addFooters(pdf);
        return pdf.save();
    }

    private async appendPdf(target: PDFDocument, file: Blob) {
        // receipts often carry an owner password (print/edit restrictions) but open without one
        const source = await PDFDocument.load(await file.arrayBuffer(), { ignoreEncryption: true });
        const pages = await target.copyPages(source, source.getPageIndices());
        pages.forEach((page) => target.addPage(page));
    }

    private async appendImage(target: PDFDocument, file: Blob) {
        const bytes = await file.arrayBuffer();
        const image =
            file.type === 'image/jpeg'
                ? await target.embedJpg(bytes)
                : file.type === 'image/png'
                  ? await target.embedPng(bytes)
                  : null;
        if (!image) throw new Error(`Unsupported image type: ${file.type}`);

        const scale = Math.min((PAGE_WIDTH - 50) / image.width, (PAGE_HEIGHT - 100) / image.height);
        const width = image.width * scale;
        const height = image.height * scale;

        target.addPage([PAGE_WIDTH, PAGE_HEIGHT]).drawImage(image, {
            x: (PAGE_WIDTH - width) / 2,
            y: (PAGE_HEIGHT - height) / 2,
            width,
            height,
        });
    }

    private async appendPlaceholder(target: PDFDocument, attachmentNumber: number, type: string | undefined) {
        const font = await target.embedFont(StandardFonts.Helvetica);
        const page = target.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        const lines = [
            `Attachment ${attachmentNumber}${type ? ` (${type})` : ''} could not be included in this report.`,
            'Download it from the request to view it.',
        ];
        lines.forEach((text, i) =>
            page.drawText(text, { x: 50, y: PAGE_HEIGHT - 100 - i * 18, size: 12, font, color: rgb(0, 0, 0) })
        );
    }

    private async addFooters(pdf: PDFDocument) {
        const font = await pdf.embedFont(StandardFonts.Helvetica);
        const fontSize = 10;
        const textOptions = { y: 30, size: fontSize, font, color: rgb(0, 0, 0) };
        const runOn = `Report run on ${this.datePipe.transform(new Date(), DateFormat.dateTime12, 'utc')} UTC`;
        const pages = pdf.getPages();

        pages.forEach((page, index) => {
            const pageNumber = `Page ${index + 1} of ${pages.length}`;
            page.drawText(runOn, { ...textOptions, x: 25 });
            page.drawText(pageNumber, {
                ...textOptions,
                x: page.getWidth() - font.widthOfTextAtSize(pageNumber, fontSize) - 25,
            });
        });
    }

    private async createReport({
        request,
        patient,
        visitName,
        availableExpenses,
        showConvertedAmounts,
    }: ExpenseReportData): Promise<Blob> {
        const docDefinition = {
            pageSize: 'A4',
            pageMargins: [25, 25, 25, 25],
            content: [
                header(),
                spacer(),
                this.requestSummaryTable(request, patient, visitName),
                spacer(),
                this.expensesTable(request, patient, availableExpenses, showConvertedAmounts),
                ...(showConvertedAmounts ? [this.totals(request.reimbursementTransactions, patient)] : []),
            ],
            styles: reportStyles,
        };

        await this.loadPdfMake();
        return this.pdfMake.createPdf(docDefinition).getBlob();
    }

    private requestSummaryTable(request: ReimbursementRequest, patient: Patient, visitName: VisitName | undefined) {
        const label = (text: string) => this.cell(text, 'summaryRowTitle', 'left', marginSummary);
        const value = (text: string) => this.cell(text, 'tableRow', 'left', marginSummary);
        const identification = patient?.patientIdentification;

        return {
            layout: plainLayout,
            table: {
                widths: ['*', '*', '*', '*'],
                body: [
                    [label('Request ID:'), value(this.format(request.requestPseudoId)), value(''), value('')],
                    [
                        label('Visit Name:'),
                        value(visitName ? this.format(visitName.name) : 'Unspecified'),
                        label('Visit Date:'),
                        value(this.format(request.visitStartDate)),
                    ],
                    [
                        label('Patient ID:'),
                        value(identification ? this.format(identification.patientId) : 'Unspecified'),
                        label('Patient Name:'),
                        value(
                            identification ? `${identification.firstName} ${identification.lastName}` : 'Unspecified'
                        ),
                    ],
                ],
            },
        };
    }

    private expensesTable(
        request: ReimbursementRequest,
        patient: Patient,
        availableExpenses: StudyExpense[],
        showConvertedAmounts: boolean
    ) {
        const patientCurrency = patient?.preferredCurrency?.toUpperCase();
        const title = (text: string, alignment: Alignment) => this.cell(text, 'tableTitle', alignment);
        const expenseType = (expense: Expense) =>
            availableExpenses.find((x) => x.expenseId == expense.expenseId)?.expenseDefaultValue;
        // timestamp is an ISO string without offset; format it as local, same as the expense views
        const receiptDate = (expense: Expense) =>
            this.datePipe.transform(expense.timestamp, DateFormat.dateOnly, 'local') ?? '';
        const amount = (value: number | null, currency = '', alignment: Alignment = 'left') =>
            this.cell(
                value == null ? '-' : `${this.decimalPipe.transform(value, '1.2-2', 'en-US')} ${currency}`.trim(),
                'tableRow',
                alignment
            );
        // for USD patients the patient-currency column would duplicate the USD one
        const showPatientCurrency = showConvertedAmounts && patientCurrency !== 'USD';
        const columnCount = 5 + (showPatientCurrency ? 1 : 0) + (showConvertedAmounts ? 1 : 0);

        return {
            layout: expensesTableLayout,
            table: {
                widths: Array(columnCount).fill('*'),
                body: [
                    [
                        title('Expense Type', 'left'),
                        title('Receipt Date', 'left'),
                        title('Requested Amount', 'right'),
                        title('Authorized Amount', 'right'),
                        ...(showPatientCurrency
                            ? [
                                  title(
                                      patientCurrency ? `Authorized Amount (${patientCurrency})` : 'Authorized Amount',
                                      'right'
                                  ),
                              ]
                            : []),
                        ...(showConvertedAmounts ? [title('Authorized Amount (USD)', 'right')] : []),
                        title('Notes', 'left'),
                    ],
                    ...request.reimbursementTransactions.map((expense) => [
                        this.cell(expenseType(expense), 'tableRow', 'left'),
                        this.cell(receiptDate(expense), 'tableRow', 'left'),
                        amount(expense.amount, expense.currency, 'right'),
                        amount(expense.authorizedAmount, expense.currency, 'right'),
                        ...(showPatientCurrency ? [amount(expense.authorizedAmountPatientsCurrency, '', 'right')] : []),
                        ...(showConvertedAmounts ? [amount(expense.authorizedAmountUsd, '', 'right')] : []),
                        this.cell(expense.notes?.trim() || '-', 'tableRow', 'left'),
                    ]),
                ],
            },
        };
    }

    private totals(expenses: Expense[], patient: Patient) {
        const patientCurrency = patient?.preferredCurrency?.toUpperCase();
        const showPatientCurrency = patientCurrency !== 'USD';
        // empty '*' column pushes the labels and amounts right; labels share a left edge, amounts a right edge
        const line = (label: string, values: (number | null | undefined)[]) => {
            // missing amounts are skipped; if none are set the total is unknown, same as the backend totals
            const present = values.filter((v) => v != null);
            const total = present.length ? present.reduce((sum, v) => sum + v, 0) : null;
            return [
                '',
                this.cell(label, 'tableRow', 'left'),
                this.cell(
                    total == null ? '-' : this.decimalPipe.transform(total, '1.2-2', 'en-US'),
                    'tableRow',
                    'right'
                ),
            ];
        };

        return {
            layout: plainLayout,
            table: {
                widths: ['*', 'auto', 'auto'],
                body: [
                    ...(showPatientCurrency
                        ? [
                              line(
                                  patientCurrency ? `Total Authorized (${patientCurrency}):` : 'Total Authorized:',
                                  expenses.map((e) => e.authorizedAmountPatientsCurrency)
                              ),
                          ]
                        : []),
                    line(
                        'Total Authorized (USD):',
                        expenses.map((e) => e.authorizedAmountUsd)
                    ),
                ],
            },
        };
    }

    private cell(text: string, style: string, alignment: Alignment, margin = marginTable) {
        return { text, style, alignment, margin: [...margin], verticalAlignment: 'middle' };
    }

    private format(value: string | number | Moment | null): string {
        if (isMoment(value)) return this.datePipe.transform(value.toDate(), DateFormat.dateOnly, 'utc');
        if (!isNaN(+value)) return this.decimalPipe.transform(+value, '1.2-2', 'en-US');
        return value?.toString();
    }

    private async loadPdfMake() {
        if (this.pdfMake) return;

        const pdfMakeModule = await import('pdfmake/build/pdfmake');
        const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
        this.pdfMake = pdfMakeModule.default;
        this.pdfMake.addVirtualFileSystem(pdfFontsModule.default);
    }
}
